import { ServiceLogo } from "@/components/ServiceLogo";
import { useEffect, useState } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import OfferCard from "@/components/OfferCard";
import { Search, MapPin, Sparkles, Clock, Infinity, LocateFixed, X, Loader2 } from "lucide-react";
import { useGeolocation } from "@/hooks/useGeolocation";
import { toast } from "sonner";
import { useSeo } from "@/hooks/useSeo";

const QUICK_FILTERS = [
  { key: "near",      label: "Autour de moi",     icon: LocateFixed, color: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  { key: "new",       label: "Nouveautés",        icon: Sparkles,    color: "bg-orange-50 text-orange-800 border-orange-200" },
  { key: "expiring",  label: "Expire bientôt",    icon: Clock,       color: "bg-amber-50 text-amber-800 border-amber-200" },
  { key: "permanent", label: "Offres permanentes", icon: Infinity,   color: "bg-blue-50 text-blue-800 border-blue-200" },
];

const RADII = [
  { v: "5",  l: "- 5 km" },
  { v: "10", l: "- 10 km" },
  { v: "20", l: "- 20 km" },
  { v: "",   l: "Tout l'Eure-et-Loir" },
];

export default function Deals() {
  const loc = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const sp = new URLSearchParams(loc.search);

  const [q, setQ] = useState(sp.get("q") || "");
  const [category, setCategory] = useState(sp.get("category") || params.categorySlug || "");
  const [city, setCity] = useState(sp.get("city") || "");
  const [coverage, setCoverage] = useState(sp.get("coverage") || "");
  const [sort, setSort] = useState("recent");
  const [quick, setQuick] = useState("");
  const [radius, setRadius] = useState("10");
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const geo = useGeolocation();
  useSeo({
    title: "Tous les bons plans d'Eure-et-Loir | PROXIreduc-28",
    description: "Des réductions chez vos commerces de proximité toute l'année. Créez votre compte gratuit et profitez-en dès aujourd'hui.",
    canonicalPath: "/bons-plans",
    image: "/og-bons-plans.jpg",
  });

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  }, []);

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (category) p.set("category", category);
    if (city) p.set("city", city);
    if (coverage) p.set("coverage", coverage);
    if (sort) p.set("sort", sort);
    if (quick === "new") p.set("is_new", "1");
    if (quick === "expiring") { p.set("expiring_soon", "1"); p.set("sort", "expiration"); }
    if (quick === "permanent") p.set("permanent", "1");
    if (quick === "near") {
      if (geo.coords) {
        p.set("near_lat", String(geo.coords.lat));
        p.set("near_lng", String(geo.coords.lng));
      } else if (city) {
        p.set("near_city", city);
      }
      if (radius) p.set("radius_km", radius);
      p.set("sort", "recent"); // le backend trie par distance quand near_* est présent
    }
    try {
      const { data } = await api.get(`/offers?${p.toString()}`);
      setOffers(data);
    } catch (e) {
      toast.error("Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, city, sort, coverage, quick, radius, geo.coords]);

  // SEO dynamique
  useEffect(() => {
    const cityLabel = city ? ` à ${city}` : "";
    const catLabel = category ? ` — ${cats.find((c)=>c.slug===category)?.name || category}` : "";
    document.title = `Bons plans${cityLabel}${catLabel} | PROXIreduc-28`;
    const desc = city
      ? `Tous les bons plans et avantages des commerces partenaires de ${city} en Eure-et-Loir. Économisez localement avec PROXIreduc-28.`
      : "Tous les bons plans et avantages proposés par les commerçants et professionnels d'Eure-et-Loir : Chartres, Dreux, Châteaudun, Épernon, Nogent, Brou, Bonneval.";
    let md = document.querySelector('meta[name="description"]');
    if (!md) { md = document.createElement("meta"); md.name = "description"; document.head.appendChild(md); }
    md.setAttribute("content", desc);
  }, [city, category, cats]);

  const submit = (e) => { e.preventDefault(); load(); };

  const handleQuickClick = async (key) => {
    if (key === "near") {
      if (!geo.coords) {
        toast.info("Autorisez la géolocalisation pour trouver les offres autour de vous.");
        const c = await geo.request();
        if (!c) return;
      }
    }
    setQuick((prev) => (prev === key ? "" : key));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-6 flex items-start gap-4">
        <ServiceLogo serviceKey="bons-plans" size={64} className="mt-1" />
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold" data-testid="deals-title">Tous les bons plans</h1>
          <p className="text-slate-500 mt-1">Filtrez par catégorie, ville, distance ou disponibilité.</p>
          {coverage === "department" && (
            <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 text-amber-300 text-xs font-bold uppercase tracking-widest">
              Offres valables dans tout l'Eure-et-Loir
            </div>
          )}
        </div>
      </div>

      {/* Form principal */}
      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2 flex items-center gap-2 px-3 border border-slate-200 rounded-lg">
          <Search size={16} className="text-slate-400"/>
          <Input data-testid="deals-search-input" value={q} onChange={(e)=>setQ(e.target.value)} placeholder="Mot-clé…" className="border-0 shadow-none focus-visible:ring-0"/>
        </div>
        <Select value={category} onValueChange={(v)=>setCategory(v==="all"?"":v)}>
          <SelectTrigger data-testid="deals-category-select"><SelectValue placeholder="Toutes catégories"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {cats.map((c)=>(<SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={city} onValueChange={(v)=>setCity(v==="all"?"":v)}>
          <SelectTrigger data-testid="deals-city-select"><SelectValue placeholder="Toutes villes"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes villes</SelectItem>
            {cities.map((c)=>(<SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="deals-sort-select"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Plus récents</SelectItem>
            <SelectItem value="popular">Populaires</SelectItem>
            <SelectItem value="expiration">Expiration proche</SelectItem>
          </SelectContent>
        </Select>
        <Button type="submit" className="btn-shine text-white md:col-span-1 col-span-1" data-testid="deals-search-submit">Filtrer</Button>
      </form>

      {/* Filtres rapides */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="deals-quick-filters">
        {QUICK_FILTERS.map((f) => {
          const active = quick === f.key;
          return (
            <button key={f.key} onClick={() => handleQuickClick(f.key)}
                    data-testid={`deals-quick-${f.key}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${active ? "bg-slate-900 text-white border-slate-900" : f.color}`}>
              {f.key === "near" && geo.loading ? <Loader2 size={13} className="animate-spin"/> : <f.icon size={13}/>}
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Sous-filtres distance quand "Autour de moi" actif */}
      {quick === "near" && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="deals-radius-row">
          <span className="text-xs font-bold text-emerald-800 uppercase tracking-widest flex items-center gap-1"><MapPin size={13}/> Distance :</span>
          {RADII.map((r) => (
            <button key={r.v} onClick={() => setRadius(r.v)} data-testid={`deals-radius-${r.v || "all"}`}
                    className={`px-3 py-1 rounded-full text-xs font-bold border ${radius === r.v ? "bg-emerald-700 text-white border-emerald-700" : "bg-white border-emerald-200 text-emerald-800 hover:border-emerald-500"}`}>
              {r.l}
            </button>
          ))}
          {geo.coords && (
            <button onClick={() => { geo.clear(); setQuick(""); }} className="ml-auto inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900" data-testid="deals-geo-clear">
              <X size={13}/> Désactiver la géoloc
            </button>
          )}
          {geo.error && <div className="w-full text-xs text-rose-700">{geo.error}</div>}
          {!geo.coords && (
            <div className="w-full text-xs text-emerald-800" data-testid="deals-near-city-hint">
              {city ? `Offres autour de ${city}` : "Astuce : activez la localisation ou choisissez une commune ci-dessus pour voir les offres autour de vous."}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 text-sm text-slate-500" data-testid="deals-count">
        {loading ? "Chargement…" : `${offers.length} offre${offers.length > 1 ? "s" : ""}`}
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {offers.map((o)=>(<OfferCard key={o.id} offer={o}/>))}
        {!loading && offers.length === 0 && (
          <div className="col-span-full text-center py-16 text-slate-500" data-testid="deals-empty">Aucune offre ne correspond à vos critères.</div>
        )}
      </div>
    </div>
  );
}
